import type {
  TimeoutCreateAnswer,
  TimeoutCreateRequest,
  TimeoutPeriod,
  TimeoutRow,
  TimeoutUpdateForm,
} from '../types/timeout'
import { toLastKnownClock } from './periodClock'
import { toTimeoutClock } from './timeoutClock'

/**
 * A tombstone is the API's answer to a repeated create of a timeout that has
 * been deleted since: the row, with the time it was deleted.
 */
export const isTimeoutTombstone = (row: TimeoutRow): boolean =>
  typeof row.deleted_at === 'string'

/** The API answers a change with the whole row, which always carries its id. */
const isFullRow = (
  update: TimeoutRow | TimeoutUpdateForm
): update is TimeoutRow => typeof (update as TimeoutRow).id === 'string'

/**
 * The list after a timeout is recorded: added, or put in place of the row with
 * the same id (a repeated request answers with the row already stored).
 *
 * A tombstone is never added. It takes the row out instead: the timeout was
 * deleted, on this device or another, and must not come back.
 */
export const upsertTimeout = (
  timeouts: ReadonlyArray<TimeoutRow> | null | undefined,
  row: TimeoutRow
): TimeoutRow[] => {
  const stored = timeouts || []
  if (isTimeoutTombstone(row)) {
    return stored.filter((timeout) => timeout.id !== row.id)
  }
  return stored.some((timeout) => timeout.id === row.id)
    ? stored.map((timeout) => (timeout.id === row.id ? row : timeout))
    : [...stored, row]
}

/**
 * The list after a timeout's clock is changed. The API's answer replaces the
 * stored row; a change made while offline carries the new clock only and is
 * laid over it. A timeout that is not in the list stays out of it.
 */
export const applyTimeoutUpdate = (
  timeouts: ReadonlyArray<TimeoutRow> | null | undefined,
  timeoutId: string,
  update: TimeoutRow | TimeoutUpdateForm
): TimeoutRow[] =>
  (timeouts || []).map((timeout) => {
    if (timeout.id !== timeoutId) {
      return timeout
    }
    return isFullRow(update) ? update : { ...timeout, ...update }
  })

/** The list without the given timeout. */
export const removeTimeout = (
  timeouts: ReadonlyArray<TimeoutRow> | null | undefined,
  timeoutId: string
): TimeoutRow[] =>
  (timeouts || []).filter((timeout) => timeout.id !== timeoutId)

export interface TimeoutCounts {
  home: number
  visiting: number
}

/** How many timeouts each team has called in the whole game. */
export const countTimeoutsByTeam = (
  timeouts: ReadonlyArray<TimeoutRow> | null | undefined,
  homeTeamId?: string | null,
  visitingTeamId?: string | null
): TimeoutCounts => {
  const counts: TimeoutCounts = { home: 0, visiting: 0 }
  ;(timeouts || []).forEach((timeout) => {
    if (homeTeamId && timeout.team_id === homeTeamId) {
      counts.home += 1
    } else if (visitingTeamId && timeout.team_id === visitingTeamId) {
      counts.visiting += 1
    }
  })
  return counts
}

/**
 * Whether the game has a timeout on record. The timeline and the game's stats
 * show their Timeouts row only then, whether or not the competition still
 * tracks timeouts: a game with none looks as it always has, and one whose
 * timeouts were recorded keeps them on show after tracking is switched off.
 */
export const hasRecordedTimeouts = (
  timeouts: ReadonlyArray<TimeoutRow> | null | undefined
): boolean => (timeouts || []).length > 0

export interface TimeoutsRowState {
  timeouts: ReadonlyArray<TimeoutRow> | null | undefined
  /** Whether the competition tracks timeouts. */
  trackTimeouts: boolean
  /** Whether the timeline is being edited. */
  isEditing: boolean
}

/**
 * Whether a basic-stats game's timeline shows its Timeouts row. It does once a
 * team has called one (see `hasRecordedTimeouts`). While the timeline is being
 * edited it also does wherever the competition tracks timeouts, so the first
 * one can be added there, and setting the last one to zero leaves the row in
 * place to undo it. Where timeouts are not tracked, the ones recorded can still
 * be changed, as on a full-stats game's timeline.
 */
export const showsTimeoutsRow = ({
  timeouts,
  trackTimeouts,
  isEditing,
}: TimeoutsRowState): boolean =>
  hasRecordedTimeouts(timeouts) || (isEditing && trackTimeouts)

/**
 * The row the app shows for a timeout it has queued instead of sent: the same
 * shape the API will answer with, under the id the app made for it.
 */
export const toQueuedTimeout = ({
  id,
  periodId,
  form,
}: TimeoutCreateRequest): TimeoutRow => ({
  id: form.uid,
  game_id: id,
  period_id: periodId,
  team_id: form.team_id,
  period_clock_time: toTimeoutClock(form.period_clock_time),
  occurred_at: form.occurred_at,
})

/** The period being played: started and not ended. */
export const findPeriodInPlay = <TPeriod extends TimeoutPeriod>(
  periods: ReadonlyArray<TPeriod> | null | undefined
): TPeriod | undefined =>
  (periods || []).find(
    (period) => Boolean(period.started_at) && period.ended_at === null
  )

/**
 * Whether the answer to a create moves the clock the scoring screen shows for
 * the period being played, as the API moves that period's own clock: only
 * when the timeout was recorded by this request, only in that period, and only
 * with a clock that can be read. A repeat records nothing (the API leaves the
 * period's clock alone for it), and neither does a tombstone, so neither moves
 * anything.
 */
export const movesCurrentPeriodClock = (
  periods: ReadonlyArray<TimeoutPeriod> | null | undefined,
  { data, recorded }: TimeoutCreateAnswer
): boolean =>
  recorded &&
  !isTimeoutTombstone(data) &&
  toTimeoutClock(data.period_clock_time) !== null &&
  findPeriodInPlay(periods)?.id === data.period_id

/**
 * The clock the scoring screen keeps for the period being played after a
 * timeout moves it: every minute in the minutes, so a timeout in the second
 * hour of a long period reads `65:00`, not `5:00`.
 */
export const currentPeriodClockAfter = (row: TimeoutRow): string | null =>
  toLastKnownClock(row.period_clock_time)

const HTTP_CREATED = 201

/**
 * The API's answer to a create (`POST .../timeouts`), read off its status and
 * body: 201 when this request recorded the timeout, 200 when the id was one it
 * already held.
 */
export const readTimeoutCreateAnswer = (
  status: number,
  body: { data: TimeoutRow }
): TimeoutCreateAnswer => ({
  data: body.data,
  recorded: status === HTTP_CREATED,
})
